<template>
  <div class="px-6 py-6 space-y-6 min-h-screen font-light select-none text-left transition-colors duration-200"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-gray-50 text-gray-700'">
    <div class="border-b pb-5 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900/60' : 'border-gray-200'">
      <h1 class="text-xl font-medium tracking-tight transition-colors duration-200"
        :class="isDark ? 'text-white' : 'text-gray-900'">Grand Livre des Transactions</h1>
      <p class="text-xs font-light mt-1 transition-colors duration-200"
        :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Historique complet des opérations financières.</p>
    </div>

    <div v-if="isLoading" class="flex justify-center py-20">
      <div class="text-sm transition-colors duration-200"
        :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Chargement...</div>
    </div>

    <div v-else class="border rounded-2xl overflow-hidden transition-colors duration-200"
      :class="isDark
        ? 'bg-zinc-900/20 border-zinc-900'
        : 'bg-white border-gray-200 shadow-sm'">
      <table class="w-full text-xs">
        <thead class="border-b transition-colors duration-200"
          :class="isDark
            ? 'bg-zinc-950 border-zinc-900'
            : 'bg-gray-50 border-gray-200'">
          <tr class="text-[10px] uppercase tracking-widest font-mono transition-colors duration-200"
            :class="isDark ? 'text-zinc-600' : 'text-gray-600'">
            <th class="px-6 py-4 text-left font-medium">Date & Heure</th>
            <th class="px-6 py-4 text-left font-medium">Client</th>
            <th class="px-6 py-4 text-left font-medium">Prestations / Produits</th>
            <th class="px-6 py-4 text-center font-medium">Mode de paiement</th>
            <th class="px-6 py-4 text-right font-medium">Montant Total</th>
            <th class="px-6 py-4 text-right font-medium">Réduction</th>
            <th class="px-6 py-4 text-right font-medium">Montant Payé</th>
            <th class="px-6 py-4 text-center font-medium">Statut</th>
          </tr>
        </thead>
        <tbody class="divide-y transition-colors duration-200"
          :class="isDark ? 'divide-zinc-900/40' : 'divide-gray-200'">
          <tr v-for="tx in transactions" :key="tx.id" class="transition-colors"
            :class="isDark ? 'hover:bg-zinc-900/20' : 'hover:bg-gray-50'">
            <td class="px-6 py-4 whitespace-nowrap font-mono text-[11px] transition-colors duration-200"
              :class="isDark ? 'text-zinc-400' : 'text-gray-600'">
              {{ formatDateTime(tx.date) }}
            </td>
            <td class="px-6 py-4 transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-gray-900'">{{ tx.client }}</td>
            <td class="px-6 py-4 transition-colors duration-200"
              :class="isDark ? 'text-zinc-400' : 'text-gray-600'">
              <div class="space-y-0.5">
                <div v-for="(item, idx) in tx.items" :key="idx" class="text-[11px]">
                  {{ item.qte }}x {{ item.nom }} ({{ item.prix }}€)
                </div>
              </div>
            </td>
            <td class="px-6 py-4 text-center">
              <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-medium uppercase tracking-wider" :class="{
                'bg-blue-950/40 text-blue-400 border border-blue-900/40': tx.paymentMethod === 'CB',
                'bg-emerald-950/40 text-emerald-400 border border-emerald-900/40': tx.paymentMethod === 'CASH',
                'bg-amber-950/40 text-amber-400 border border-amber-900/40': tx.paymentMethod === 'CHQ',
                'bg-purple-950/40 text-purple-400 border border-purple-900/40': tx.paymentMethod === 'LYF'
              }">
                {{ getPaymentMethodLabel(tx.paymentMethod) }}
              </span>
            </td>
            <td class="px-6 py-4 text-right font-mono transition-colors duration-200"
              :class="isDark ? 'text-zinc-400' : 'text-gray-600'">{{ tx.montantTotal.toFixed(2) }} €</td>
            <td class="px-6 py-4 text-right font-mono text-red-400">
              {{ tx.montantReduction > 0 ? `- ${tx.montantReduction.toFixed(2)} €` : '-' }}
            </td>
            <td class="px-6 py-4 text-right font-mono font-semibold text-sm transition-colors duration-200"
              :class="isDark ? 'text-[#D5CCBC]' : 'text-amber-600'">
              {{ tx.montantFinal.toFixed(2) }} €
            </td>
            <td class="px-6 py-4 text-center">
              <span class="inline-flex px-2 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider" :class="{
                'bg-emerald-950/40 text-emerald-400': tx.status === 'paid',
                'bg-amber-950/40 text-amber-400': tx.status === 'pending',
                'bg-red-950/40 text-red-400': tx.status === 'refunded'
              }">
                {{ tx.status === 'paid' ? 'Payé' : tx.status === 'pending' ? 'En attente' : 'Remboursé' }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="transactions.length === 0" class="text-center py-12 transition-colors duration-200"
        :class="isDark ? 'text-zinc-600' : 'text-gray-500'">Aucune transaction</div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useTheme } from '@/composables/useTheme'
import api from '@/api'

// === Thème ===
const { isDark } = useTheme()

const isLoading = ref(true)
const transactions = ref([])

onMounted(async () => {
  try {
    const response = await api.transactions.getAllTransactions()
    // Backend already returns formatted data
    transactions.value = (response.data || []).map(tx => ({
      id: tx.id,
      date: tx.date,
      client: tx.client || 'Client anonyme',
      items: tx.items || [],
      paymentMethod: tx.paymentMethod,
      montantTotal: tx.montantTotal || 0,
      montantReduction: tx.montantReduction || 0,
      montantFinal: tx.montantFinal || 0,
      status: tx.status || 'paid'
    }))
  } catch (error) {
    console.error('Erreur lors du chargement des transactions:', error)
  } finally {
    isLoading.value = false
  }
})

const formatDateTime = (date) => {
  if (!date) return '-'
  const d = new Date(date)
  const dateStr = d.toLocaleDateString('fr-FR')
  const timeStr = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  return `${dateStr} ${timeStr}`
}

const getPaymentMethodLabel = (method) => {
  const labels = {
    'CB': 'Carte Bancaire',
    'CASH': 'Espèces',
    'CHQ': 'Chèque',
    'LYF': 'Lien sans contact',
    'STRIPE': 'Stripe',
    'OTHER': 'Autre'
  }
  return labels[method] || method
}
</script>
